import { SearchQueryBody, ConditionGroup, SimpleCondition } from "@/types";

import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";

import {
  ActionRecord,
  CreateActionRequest,
  InvocationRecord,
  InvocationStatus,
  UpdateActionRequest,
} from "./ActionRecord";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
}

export type ActionQueryScope = "org" | "actionhub";

interface QueryActionsOptions {
  abortSignal: AbortSignal;
  orgId: string;
  pageToken: string;
  limit: number;
}

export class ActionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createAction(
    request: CreateActionRequest,
    orgId: string,
  ): Promise<ActionRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/actions");

    const response = await this.#httpClient.post(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });

    return response.json<ActionRecord>();
  }

  public async deleteAction(
    name: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "deleteAction requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/actions/${name}`);
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.throwIfError();
  }

  public async getAction(
    name: string,
    digest?: string,
    options?: Partial<Options>,
  ): Promise<ActionRecord> {
    if (!options?.resourceOwnerId) {
      throw Error("getAction requires a resource owner ID, none was provided");
    }

    const searchParams = new URLSearchParams();
    if (digest) {
      searchParams.append("digest", digest);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/${name}`,
      searchParams,
    );

    return this.#httpClient
      .get(requestUrl, {
        headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
        signal: options?.abortSignal,
      })
      .then((response) => response.json<ActionRecord>());
  }

  /**
   * Fetch every action visible in the given scope ("org" for the caller's
   * organization, "actionhub" for actions published to the Action Hub),
   * following `next_token` until the server reports no more pages.
   */
  public async listActions(
    scope: ActionQueryScope,
    options?: Partial<QueryActionsOptions>,
  ): Promise<ActionRecord[]> {
    const items: ActionRecord[] = [];
    let pageToken: string | undefined = options?.pageToken;

    while (true) {
      const page = await this.queryActions(scope, {
        ...options,
        pageToken,
      });

      items.push(...page.items);

      const nextToken = page.next_token ?? undefined;
      if (nextToken === undefined) {
        return items;
      }

      pageToken = nextToken;
    }
  }

  /**
   * Fetch one page of actions in the given scope ("org" for the caller's
   * organization, "actionhub" for actions published to the Action Hub).
   */
  public async queryActions(
    scope: ActionQueryScope,
    options?: Partial<QueryActionsOptions>,
  ): Promise<PaginatedResponse<ActionRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      scope === "actionhub" ? "v1/actions/query/actionhub" : "v1/actions/query",
    );

    const body: { after?: string; limit?: number } = {};
    if (options?.pageToken !== undefined) {
      body.after = options.pageToken;
    }
    if (options?.limit !== undefined) {
      body.limit = options.limit;
    }

    const response = await this.#httpClient.post(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(body),
    });

    return response.json<PaginatedResponse<ActionRecord>>();
  }

  public async updateAction(
    name: string,
    updates: UpdateActionRequest,
    options?: Partial<Options>,
  ): Promise<ActionRecord> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "updateAction requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/actions/${name}`);

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<ActionRecord>();
  }

  public async countActiveInvocationsForDataset(
    orgId: string,
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<number> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/count/active`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();

    return Number.parseInt(await response.text(), 10);
  }

  public async countTerminalInvocationsForDataset(
    orgId: string,
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<number> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/count/terminal`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();

    return Number.parseInt(await response.text(), 10);
  }

  public async queryActiveInvocationsForDataset(
    orgId: string,
    datasetId: string,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/query`,
    );

    const datasetCondition: SimpleCondition = {
      field: "data_source_id",
      comparator: "EQUALS",
      value: datasetId,
    };

    const conditions = [
      InvocationStatus.Completed,
      InvocationStatus.Cancelled,
      InvocationStatus.Failed,
      InvocationStatus.Deadly,
    ].map((status) => {
      const condition: SimpleCondition = {
        field: "last_status",
        comparator: "NOT_EQUALS",
        value: status,
      };

      return condition;
    });

    const nonTerminalCondition: ConditionGroup = {
      conditions: conditions,
      operator: "AND",
    };

    const searchConditions: ConditionGroup = {
      conditions: [datasetCondition, nonTerminalCondition],
      operator: "AND",
    };

    const searchBody: SearchQueryBody = {
      condition: searchConditions,
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal,
      body: JSON.stringify(searchBody),
    });

    return await response.json<{
      items: InvocationRecord[];
      next_token: string | null;
    } | null>();
  }

  public async queryRecentInvocationsForDataset(
    orgId: string,
    datasetId: string,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/query`,
    );

    const datasetCondition: SimpleCondition = {
      field: "data_source_id",
      comparator: "EQUALS",
      value: datasetId,
    };

    const searchConditions: ConditionGroup = {
      conditions: [datasetCondition],
      operator: "AND",
    };

    const searchBody: SearchQueryBody = {
      condition: searchConditions,
      sort_by: "created",
      sort_direction: "DESC",
      // Limit results to avoid fetching too many invocations
      limit: 50,
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal,
      body: JSON.stringify(searchBody),
    });

    return response.json<{
      items: InvocationRecord[];
      next_token: string | null;
    } | null>();
  }

  public async getInvocation(
    invocationId: string,
    orgId?: string,
    options?: Partial<Options>,
  ): Promise<InvocationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/${invocationId}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    return response.json<InvocationRecord>();
  }
}
